import React, { Dispatch, FunctionComponent, useState } from "react";

/* Validation */
import { Formik, useFormikContext } from "formik";
import { validationSchema } from "./formConfig";
import CustomError from "./CustomError";

/* API */
import axios from "axios";
import { Endpoints } from "@internal/common";
import { ClaimReportTheftFormData } from "./ClaimModels";
import { Button, Flex, Text, Title } from "@gjensidige/builders-components";
import FormInput from "./components/FormInput";
import FormTextArea from "./components/FormTextArea";
import FormInputGroup from "./components/FormInputGroup";
import FormDropdown from "./components/FormDropdown";
import FormAlert from "./components/FormAlert";
import FormDatepicker from "./components/FormDatepicker";

export interface IProps {
  claimType: string;
}

interface FormData extends ClaimReportTheftFormData {
  claimType: string;
}

const ClaimReportTheft: React.FC<IProps> = (props) => {
  const [formData, setFormData] = useState<FormData>({
    claimType: props.claimType,
    companyName: "",
    cvrNumber: "",
    policyNumber: "",
    contactPersonNameAndEmployment: "",
    email: "",
    contactPersonPhoneNumber: "",
    theftLocation: "",
    sceneOfAccidentAddress: "",
    timeOfAccident: "",
    theftDiscovered: "",
    theftDetails: "",
    carSecurelyLocked: "",
    windowsClosed: "",
    alarmActivated: "",
    theftReportedDate: "",
    policeStation: "",
    whoReportedDamage: "",
    stolenItemsBelongToPolicyholder: "",
    stolenItemsBelongToOther: "",
    insuredByOthers: "",
    insuranceCompanyName: "",
    totalClaim: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    setIsLoading(true);
    axios
      .post(Endpoints.ClaimReportDIByggeri, formData)
      .then(() => setSubmitted(true))
      .catch(() => {
        setIsLoading(false);
        setApiError(true);
      });
  };

  const openPdfInNewTab = () => {
    window.open('https://www.gjensidige.dk/filer/partner/Tyveri-genstande-di-byggeri.pdf', '_blank');
  };

  return (
    <>
      {submitted ? (
        <FormAlert
          severity="success"
          title="Tak for din anmeldelse"
          text="Vi sender dig en kvitteringsmail med informationerne fra din skadeanmeldelse."
        />
      ) : (
        <>
          <Title size={5}>Anmeldelse af tyveri</Title>
          <br />
          <Text>
            Anmeld skaden til Politiet først og eftersend Politiets kvittering
            for anmeldelsen samt opgørelse over de stjålne genstande
            til info@bygfs.dk. For elværktøj skal opgørelsen indeholde fabrikat,
            type, årgang og pris.
          </Text>
          <br />
          <Button variant={"primary"} onClick={openPdfInNewTab}>
            Download pdf til opgørelse over de stjålne genstande
          </Button>
          <br />
          <br />
          <Text size="small">* Påkrævede felter</Text>

          <Formik
            enableReinitialize
            initialValues={{
              claimType: formData.claimType,
              companyName: formData.companyName,
              cvrNumber: formData.cvrNumber,
              policyNumber: formData.policyNumber,
              contactPersonNameAndEmployment:
                formData.contactPersonNameAndEmployment,
              email: formData.email,
              contactPersonPhoneNumber: formData.contactPersonPhoneNumber,
              theftLocation: formData.theftLocation,
              sceneOfAccidentAddress: formData.sceneOfAccidentAddress,
              timeOfAccident: formData.timeOfAccident,
              theftDiscovered: formData.theftDiscovered,
              theftDetails: formData.theftDetails,
              carSecurelyLocked: formData.carSecurelyLocked,
              windowsClosed: formData.windowsClosed,
              alarmActivated: formData.alarmActivated,
              theftReportedDate: formData.theftReportedDate,
              policeStation: formData.policeStation,
              whoReportedDamage: formData.whoReportedDamage,
              stolenItemsBelongToPolicyholder:
                formData.stolenItemsBelongToPolicyholder,
              stolenItemsBelongToOther: formData.stolenItemsBelongToOther,
              insuredByOthers: formData.insuredByOthers,
              insuranceCompanyName: formData.insuranceCompanyName,
              totalClaim: formData.totalClaim,
              consent: formData.consent,
            }}
            validationSchema={validationSchema(formData)}
            onSubmit={() => {
              sendFormData();
            }}
          >
            <RenderForm isLoading={isLoading} setFormData={setFormData} />
          </Formik>
        </>
      )}
      {apiError && (
        <FormAlert
          severity="error"
          text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
          title="Der skete en fejl."
        />
      )}
    </>
  );
};

interface RenderProps {
  isLoading: boolean;
  setFormData: Dispatch<React.SetStateAction<FormData>>;
}

const RenderForm: FunctionComponent<RenderProps> = ({
  isLoading,
  setFormData,
}) => {
  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values: formData,
  } = useFormikContext<FormData>();

  const theftLocationDropdown = [
    "Ejendom",
    "Bil",
    "Byggeplads",
    "Offentligt rum",
  ];

  const todaysDate = new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <FormInput
          id="companyName"
          labelText="Virksomhedens navn *"
          name="companyName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("companyName", event.target.value);
            setFormData({
              ...formData,
              companyName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.companyName && errors.companyName)}
          errorText={errors.companyName}
          value={formData.companyName}
        />
        <FormInput
          id="cvr-number"
          labelText="CVR-nummer *"
          name="cvrNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("cvrNumber", event.target.value);
            setFormData({
              ...formData,
              cvrNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.cvrNumber && errors.cvrNumber)}
          errorText={errors.cvrNumber}
          value={formData.cvrNumber}
        />
        <FormInput
          id="policy-number"
          labelText="Policenummer *"
          name="policyNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("policyNumber", event.target.value);
            setFormData({
              ...formData,
              policyNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.policyNumber && errors.policyNumber)}
          errorText={errors.policyNumber}
          value={formData.policyNumber}
        />
        <FormInput
          id="contactPersonNameAndEmployment"
          labelText="Navn og stilling (kontaktperson) *"
          name="contactPersonNameAndEmployment"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonNameAndEmployment", event.target.value);
            setFormData({
              ...formData,
              contactPersonNameAndEmployment: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.contactPersonNameAndEmployment &&
              errors.contactPersonNameAndEmployment
            )
          }
          errorText={errors.contactPersonNameAndEmployment}
          value={formData.contactPersonNameAndEmployment}
        />
        <FormInput
          id="email"
          labelText="Email (kontaktperson) *"
          name="email"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("email", event.target.value);
            setFormData({
              ...formData,
              email: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.email && errors.email)}
          errorText={errors.email}
          value={formData.email}
        />
        <FormInput
          id="contactPersonPhoneNumber"
          labelText="Telefonnummer (kontaktperson) *"
          name="contactPersonPhoneNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              contactPersonPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.contactPersonPhoneNumber &&
              errors.contactPersonPhoneNumber
            )
          }
          errorText={errors.contactPersonPhoneNumber}
          value={formData.contactPersonPhoneNumber}
        />
        <div>
          <Title size={5}>Om tyveriet</Title>
        </div>
        <FormDropdown
          id="theftLocation"
          labelText="Tyveriet skete fra *"
          options={theftLocationDropdown.map((option) => {
            return { labelText: option, value: option };
          })}
          value={formData.theftLocation}
          onChange={(values, value) => {
            setFormData({ ...formData, theftLocation: value });
            setFieldValue("typeOfDamage", value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.theftLocation && errors.theftLocation)}
          errorText={errors.theftLocation}
        />
        <FormTextArea
          id="sceneOfAccidentAddress"
          labelText="Skadestedets adresse *"
          maxLength={500}
          placeholder="Vejnavn og nummer, by samt postnummer"
          value={formData.sceneOfAccidentAddress}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("sceneOfAccidentAddress", event.target.value);
            setFormData({
              ...formData,
              sceneOfAccidentAddress: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.sceneOfAccidentAddress && errors.sceneOfAccidentAddress)
          }
          errorText={errors.sceneOfAccidentAddress}
        />
        <FormInput
          id="timeOfAccident"
          labelText="Tidspunkt for tyveri *"
          placeholder="Dato og klokkeslæt/tidsrum"
          name="timeOfAccident"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("timeOfAccident", event.target.value);
            setFormData({
              ...formData,
              timeOfAccident: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.timeOfAccident && errors.timeOfAccident)}
          errorText={errors.timeOfAccident}
          value={formData.timeOfAccident}
        />
        <FormInput
          id="theftDiscovered"
          labelText="Hvornår blev tyveriet opdaget? *"
          placeholder="Dato og klokkeslæt"
          name="theftDiscovered"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("theftDiscovered", event.target.value);
            setFormData({
              ...formData,
              theftDiscovered: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.theftDiscovered && errors.theftDiscovered)}
          errorText={errors.theftDiscovered}
          value={formData.theftDiscovered}
        />
        <FormTextArea
          id="theftDetails"
          labelText="Hvordan mener du, tyveriet er foregået? *"
          maxLength={500}
          placeholder="Beskriv gerne grundigt"
          value={formData.theftDetails}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("theftDetails", event.target.value);
            setFormData({
              ...formData,
              theftDetails: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.theftDetails && errors.theftDetails)}
          errorText={errors.theftDetails}
        />
        <FormInputGroup
          labelText="Hvis relevant: Var bilen forsvarligt aflåst? *"
          id="carSecurelyLocked"
          onChange={(event) => {
            setFormData({
              ...formData,
              carSecurelyLocked: event.at(0) ?? "",
            });
            setFieldValue("carSecurelyLocked", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.carSecurelyLocked}
          onBlur={handleBlur}
          invalid={!!(touched.carSecurelyLocked && errors.carSecurelyLocked)}
          errorText={errors.carSecurelyLocked}
        />
        <FormInputGroup
          labelText="Hvis relevant: Var alle vinduer helt lukkede? *"
          id="windowsClosed"
          onChange={(event) => {
            setFormData({
              ...formData,
              windowsClosed: event.at(0) ?? "",
            });
            setFieldValue("windowsClosed", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.windowsClosed}
          onBlur={handleBlur}
          invalid={!!(touched.windowsClosed && errors.windowsClosed)}
          errorText={errors.windowsClosed}
        />
        <FormInputGroup
          labelText="Hvis relevant: Blev tyverialarmen aktiveret? *"
          id="alarmActivated"
          onChange={(event) => {
            setFormData({
              ...formData,
              alarmActivated: event.at(0) ?? "",
            });
            setFieldValue("alarmActivated", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.alarmActivated}
          onBlur={handleBlur}
          invalid={!!(touched.alarmActivated && errors.alarmActivated)}
          errorText={errors.alarmActivated}
        />
        <FormDatepicker
          labelText="Hvornår blev tyveriet anmeldt til politiet? *"
          id="theftReportedDate"
          onChange={(event, value) => {
            setFormData({
              ...formData,
              theftReportedDate: event?.toDateString() ?? "",
            });
            setFieldValue("theftReportedDate", event?.toDateString() ?? "");
          }}
          invalid={!!(touched.theftReportedDate && errors.theftReportedDate)}
          max={todaysDate}
          errorText={errors.theftReportedDate}
          value={formData.theftReportedDate}
        />

        <FormInput
          id="policeStation"
          labelText="Til hvilken politistation? *"
          name="policeStation"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("policeStation", event.target.value);
            setFormData({
              ...formData,
              policeStation: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.policeStation && errors.policeStation)}
          errorText={errors.policeStation}
          value={formData.policeStation}
        />
        <FormInput
          id="whoReportedDamage"
          labelText="Hvem anmeldte skaden? *"
          placeholder="Fulde navn"
          name="whoReportedDamage"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("whoReportedDamage", event.target.value);
            setFormData({
              ...formData,
              whoReportedDamage: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.whoReportedDamage && errors.whoReportedDamage)}
          errorText={errors.whoReportedDamage}
          value={formData.whoReportedDamage}
        />
        <FormInputGroup
          labelText="Tilhører de stjålne/beskadigede genstande forsikringstageren? *"
          id="stolenItemsBelongToPolicyholder"
          onChange={(event) => {
            setFormData({
              ...formData,
              stolenItemsBelongToPolicyholder: event.at(0) ?? "",
            });
            setFieldValue("stolenItemsBelongToPolicyholder", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.stolenItemsBelongToPolicyholder}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.stolenItemsBelongToPolicyholder &&
              errors.stolenItemsBelongToPolicyholder
            )
          }
          errorText={errors.stolenItemsBelongToPolicyholder}
        />
        {formData.stolenItemsBelongToPolicyholder === "Nej" && (
          <FormInput
            id="stolenItemsBelongToOther"
            labelText="Hvem tilhører de? *"
            name="stolenItemsBelongToOther"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("stolenItemsBelongToOther", event.target.value);
              setFormData({
                ...formData,
                stolenItemsBelongToOther: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={
              !!(
                touched.stolenItemsBelongToOther &&
                errors.stolenItemsBelongToOther
              )
            }
            errorText={errors.stolenItemsBelongToOther}
            value={formData.stolenItemsBelongToOther}
          />
        )}
        <FormInputGroup
          labelText="Er der tegnet en forsikring mod samme skade hos noget andet selskab? *"
          id="insuredByOthers"
          onChange={(event) => {
            setFormData({
              ...formData,
              insuredByOthers: event.at(0) ?? "",
            });
            setFieldValue("insuredByOthers", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.insuredByOthers}
          onBlur={handleBlur}
          invalid={!!(touched.insuredByOthers && errors.insuredByOthers)}
          errorText={errors.insuredByOthers}
        />
        {formData.insuredByOthers === "Ja" && (
          <FormInput
            id="insuranceCompanyName"
            labelText="Hvilket forsikringsselskab? *"
            name="insuranceCompanyName"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("insuranceCompanyName", event.target.value);
              setFormData({
                ...formData,
                insuranceCompanyName: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={
              !!(touched.insuranceCompanyName && errors.insuranceCompanyName)
            }
            errorText={errors.insuranceCompanyName}
            value={formData.insuranceCompanyName}
          />
        )}
        <div>
          <Title size={5}>Det beskadigede eller stjålne</Title>
        </div>
        <FormInput
          id="totalClaim"
          labelText="Angiv det totale erstatningskrav *"
          helpText="Angiv om det er inkl. eller ekskl. moms"
          name="totalClaim"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("totalClaim", event.target.value);
            setFormData({
              ...formData,
              totalClaim: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.totalClaim && errors.totalClaim)}
          errorText={errors.totalClaim}
          value={formData.totalClaim}
        />
        <div>
          <Title size={5}>Dokumentation</Title>
        </div>
        <Text>
          Når du har trykket Anmeld Skade nedenfor, modtager du en kvittering med et dokument vedhæftet.
          Udfyld og send dokumentet til dibyggeri@gjensidige.dk. Dokumentet kan også findes på kvitteringssiden.
          Fakturer og anden dokumentation skal også sendes via mail.
        </Text>
        <br />
        <FormInput
          id="consent-id"
          labelText="Jeg erklærer herved, at foranstående oplysninger er i overensstemmelse med de faktiske forhold, og at jeg vil yde selskabet enhver assistance i forbindelse med skaden. *"
          name="consent"
          type="checkbox"
          value="consent"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("consent", event.target.checked);
            setFormData({
              ...formData,
              consent: event.target.checked,
            });
          }}
          onBlur={handleBlur}
          checked={formData.consent}
        />
        {touched.consent && errors.consent && (
          <>
            <br />
            <CustomError errorText={errors.consent} />
          </>
        )}
        <Button variant={"primary"} type="submit" flexible>
          Anmeld Skade
        </Button>
      </Flex>
    </form>
  );
};

export default ClaimReportTheft;
